import { useState, useEffect, useRef } from 'react';

const HERO_SLIDES = [
  'phase0-3.jpg',
  'phase1-3.jpg',
  'phase2-4.jpg',
  'phase3-2.jpg',
  'phase4-3.jpg',
  'phase5-4.jpg',
  'phase5-2.jpg',
  'phase5-6.jpg',
];

interface Step {
  no: number;
  date: string;
  title: string;
  copy: string;
  images: string[];
  video?: string;
}

const STEPS: Step[] = [
  {
    no: 1,
    date: '2026.09.12',
    title: '着工',
    copy: '長年、ずっと一緒にいてくれたピアノ。「君の夢を叶えるためなら」——そう言うように、静かに部屋を出ていきました。空っぽになったこの部屋から、これからどんなワクワクが生まれていくんだろう。',
    images: ['phase1-3.jpg', 'phase1-2.jpg', 'phase1-4.jpg', 'phase1-1.jpg'],
  },
  {
    no: 2,
    date: '2026.09.19',
    title: '搬入',
    copy: 'たくさんの資材と道具が、この部屋のために運び込まれてきました。ここから、本当の工事が始まります。',
    images: ['phase0-1.jpg', 'phase0-2.jpg', 'phase0-3.jpg', 'phase0-4.jpg', 'phase0-5.jpg', 'phase0-6.jpg'],
  },
  {
    no: 3,
    date: '2026.09.19',
    title: '防音・吸音',
    copy: '外の世界から、静けさを守る壁。',
    images: ['phase2-1.jpg', 'phase2-3.jpg', 'phase2-4.jpg', 'phase3-1.jpg', 'phase2-5.jpg'],
    video: 'insulation-fill.mp4',
  },
  {
    no: 4,
    date: '2026.09.19',
    title: '防音ドア・内装',
    copy: '重い一枚の扉が、音の境界になる。',
    images: ['phase3-2.jpg', 'phase3-3.jpg', 'phase5-5.jpg', 'phase3-6.jpg'],
  },
  {
    no: 5,
    date: '2026.09.19',
    title: '空調・天井',
    copy: '暗い部屋に、人の手が加わり続ける。まるで目を開けるように、灯りがともり——そして、呼吸をするように、空調が取り付けられました。',
    images: ['phase4-2.jpg', 'phase4-3.jpg', 'phase3-7.jpg'],
    video: 'light-on.mp4',
  },
  {
    no: 6,
    date: '2026.09.19',
    title: '仕上げ',
    copy: '床が張られ、パネルが並ぶ。鼓動のように、少しずつ命が宿っていく。',
    images: ['phase5-1.jpg', 'phase5-3.jpg', 'phase5-4.jpg', 'phase5-7.jpg'],
  },
  {
    no: 7,
    date: '2026.09.21',
    title: '音響調整',
    copy: 'すべての音が、正しい場所へ収まった瞬間——この部屋は、産声を上げました。',
    images: ['phase5-6.jpg'],
  },
];

const TOTAL = STEPS.length;
const LATEST = STEPS[STEPS.length - 1].date.replace(/\./g, '.');
const TOTAL_MEDIA = STEPS.reduce((n, s) => n + s.images.length, 0);

// ── オープニング:全画面スライドショー + 工程インデックス ──
function OpeningHero() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 3600);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative h-screen w-full overflow-hidden bg-black">
      {HERO_SLIDES.map((img, i) => (
        <img
          key={img}
          src={`/images/studio-journey/${img}`}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/20 to-black/80" />

      <div className="relative z-10 h-full flex flex-col justify-between px-6 md:px-14 py-10">
        <div className="flex items-center justify-between text-white/70 text-xs tracking-[0.3em]">
          <span>WAKUWAKU STUDIO</span>
          <span>CONSTRUCTION LOG</span>
        </div>

        <div>
          <p className="text-teal-300 text-xs md:text-sm tracking-[0.4em] mb-5">SPECIAL FEATURE</p>
          <h1
            className="text-white font-bold leading-[0.95] mb-6"
            style={{ fontFamily: '"Noto Serif JP", serif', fontSize: 'clamp(3rem, 10vw, 8rem)' }}
          >
            WakuWaku<br />Studio.
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-md leading-relaxed">
            音をつくる部屋が、まだ音のない部屋だった頃から。少しずつ"Studio"になっていく過程を、
            写真のタイムスタンプ順に記録しています。
          </p>
        </div>

        <div>
          {/* 工程インデックス */}
          <div className="hidden sm:flex flex-wrap items-center gap-x-3 gap-y-1 text-white/50 text-[11px] tracking-widest mb-4">
            {STEPS.map((s, i) => (
              <span key={s.no} className="flex items-center gap-3">
                <span className="hover:text-teal-300 transition-colors">
                  {String(s.no).padStart(2, '0')} {s.title}
                </span>
                {i < STEPS.length - 1 && <span className="text-white/20">/</span>}
              </span>
            ))}
          </div>
          <div className="flex items-center justify-between text-white/60 text-xs tracking-widest">
            <span>{TOTAL_MEDIA}枚の記録 ／ 最終更新 {LATEST}</span>
            <span className="flex items-center gap-2">
              SCROLL
              <i className="ri-arrow-down-line animate-bounce" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 各工程の右側メディア:自動で流れるミニスライドショー ──
type MediaItem = { type: 'image' | 'video'; src: string };

function StepMedia({ items, altBase, onImageClick }: {
  items: MediaItem[];
  altBase: string;
  onImageClick: (src: string) => void;
}) {
  const [index, setIndex] = useState(0);
  const multiple = items.length > 1;
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // 画像は一定時間で自動送り。動画は最後まで再生してから次へ進む。
  useEffect(() => {
    if (!multiple) return;
    const current = items[index];
    if (current.type === 'video') {
      const el = videoRefs.current[index];
      if (!el) return;
      const advance = () => setIndex((i) => (i + 1) % items.length);
      el.currentTime = 0;
      el.play().catch(() => {});
      el.addEventListener('ended', advance);
      return () => el.removeEventListener('ended', advance);
    }
    const timer = setTimeout(() => setIndex((i) => (i + 1) % items.length), 3400);
    return () => clearTimeout(timer);
  }, [index, multiple, items]);

  return (
    <div className="relative h-full w-full bg-black">
      {items.map((item, i) => (
        <div key={item.src} className={`absolute inset-0 transition-opacity duration-[1200ms] ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {item.type === 'video' ? (
            <video
              ref={(el) => { videoRefs.current[i] = el; }}
              src={`/videos/${item.src}`}
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <button
              type="button"
              onClick={() => onImageClick(`/images/studio-journey/${item.src}`)}
              className="block w-full h-full cursor-zoom-in group overflow-hidden"
              aria-label={`${altBase} を拡大表示`}
            >
              <img
                src={`/images/studio-journey/${item.src}`}
                alt={altBase}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </button>
          )}
        </div>
      ))}

      {multiple && (
        <div className="absolute top-8 md:top-10 right-6 md:right-14 flex gap-1.5 z-10">
          {items.map((item, i) => (
            <span
              key={item.src}
              className={`h-1 rounded-full transition-all ${i === index ? 'w-6 bg-teal-300' : 'w-1.5 bg-white/40'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── 各工程セクション:トップのヒーローと同じく、写真の中に文字を重ねる ──
function StepSection({ step, onImageClick }: { step: Step; onImageClick: (src: string) => void }) {
  const media: MediaItem[] = [
    ...(step.video ? [{ type: 'video' as const, src: step.video }] : []),
    ...step.images.map((src) => ({ type: 'image' as const, src })),
  ];

  return (
    <div className="relative h-screen w-full overflow-hidden bg-black border-t border-white/10">
      <StepMedia items={media} altBase={step.title} onImageClick={onImageClick} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/40 pointer-events-none" />

      <div className="absolute inset-0 flex flex-col justify-end px-6 md:px-14 py-10 md:py-14 pointer-events-none">
        <p className="text-white/50 text-sm tracking-wider mb-3">{step.date}</p>
        <p className="text-teal-300 text-xs font-semibold tracking-[0.3em] mb-5">
          STEP {String(step.no).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
        </p>
        <h3
          className="text-white leading-tight mb-5"
          style={{ fontFamily: '"Noto Serif JP", serif', fontSize: 'clamp(2.2rem, 6vw, 4.5rem)' }}
        >
          {step.title}
        </h3>
        <div className="w-12 h-[1px] bg-teal-300/60 mb-5" />
        <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg">{step.copy}</p>
      </div>
    </div>
  );
}

// ── フィナーレ:産声(音響調整)専用の演出 ──
function FinaleStep({ step, onImageClick }: { step: Step; onImageClick: (src: string) => void }) {
  const img = step.images[0];
  return (
    <div className="relative min-h-screen border-t border-white/10 overflow-hidden">
      <button
        type="button"
        onClick={() => onImageClick(`/images/studio-journey/${img}`)}
        className="absolute inset-0 w-full h-full cursor-zoom-in group"
        aria-label={`${step.title} を拡大表示`}
      >
        <img
          src={`/images/studio-journey/${img}`}
          alt={step.title}
          className="w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[2000ms]"
        />
      </button>
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
      <div className="absolute inset-0 bg-gradient-radial" style={{ background: 'radial-gradient(circle at 50% 60%, rgba(45,212,191,0.18), transparent 60%)' }} />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center text-center px-6 py-24">
        <p className="text-white/50 text-sm tracking-wider mb-3">{step.date}</p>
        <p className="text-teal-300 text-xs font-semibold tracking-[0.3em] mb-8">
          STEP {String(step.no).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
        </p>
        <h3
          className="text-white leading-tight mb-8"
          style={{ fontFamily: '"Noto Serif JP", serif', fontSize: 'clamp(3rem, 9vw, 7rem)' }}
        >
          {step.title}
        </h3>
        <div className="w-16 h-[1px] bg-teal-300/60 mb-8" />
        <p className="text-white/80 text-base md:text-xl leading-relaxed max-w-xl" style={{ fontFamily: '"Noto Serif JP", serif' }}>
          {step.copy}
        </p>
      </div>
    </div>
  );
}

export default function ConstructionLogSection() {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const mainSteps = STEPS.slice(0, -1);
  const finale = STEPS[STEPS.length - 1];

  return (
    <section className="bg-black">
      <OpeningHero />

      <div>
        {mainSteps.map((step) => (
          <StepSection key={step.no} step={step} onImageClick={setLightbox} />
        ))}
        <FinaleStep step={finale} onImageClick={setLightbox} />
      </div>

      <div className="relative overflow-hidden py-24 px-6 md:px-14 border-t border-white/10">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-teal-500/10 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-teal-500/10 blur-3xl" aria-hidden="true" />

        <div className="relative max-w-3xl mx-auto flex flex-col md:flex-row items-center gap-10 text-center md:text-left">
          <div className="flex -space-x-6 shrink-0">
            {['finale-1.jpg', 'phase5-2.jpg'].map((img, i) => (
              <button
                key={img}
                type="button"
                onClick={() => setLightbox(`/images/studio-journey/${img}`)}
                className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden border-4 border-black ring-1 ring-teal-300/40 shadow-xl cursor-zoom-in"
                style={{ zIndex: 2 - i }}
                aria-label="完成を喜ぶ様子を拡大表示"
              >
                <img src={`/images/studio-journey/${img}`} alt="完成を喜ぶ代表" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          <div>
            <p className="text-teal-300 text-xs tracking-[0.3em] font-semibold mb-4">NEXT UPDATE</p>
            <p className="text-white text-xl md:text-2xl font-medium mb-3" style={{ fontFamily: '"Noto Serif JP", serif' }}>
              音を、これからも記録していきます。
            </p>
            <p className="text-white/50 text-sm">この部屋から生まれる作品を、これからも見守っていてください。</p>
          </div>
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={lightbox}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg cursor-zoom-out"
          />
        </div>
      )}
    </section>
  );
}
